import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { fetchGuestOrderById } from '../lib/guestOrdersApi';

/**
 * Global watcher that automatically redirects to PaymentSuccessPage
 * if there's a pending order with a completed payment in localStorage.
 * This handles cases where iOS/Safari redirects to root instead of the return URL.
 * 
 * SIMPLIFIED: No more origin_device logic - just check if payment completed.
 * SECURITY: Uses Edge Function for guest order status checks (bypasses RLS securely).
 */
export function PendingSwishWatcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isGuest } = useAuth();
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    // Don't redirect if already on payment pages
    if (location.pathname.startsWith('/payment')) {
      return;
    }

    // Check for pending Swish order
    const pendingOrderId = localStorage.getItem('pending_swish_order_id');
    const pendingTimestamp = localStorage.getItem('pending_swish_order_timestamp');
    
    if (!pendingOrderId) return;

    // Timeout check: If order is older than 15 minutes, clear it
    if (pendingTimestamp) {
      const ageMinutes = (Date.now() - parseInt(pendingTimestamp)) / 1000 / 60;
      if (ageMinutes > 15) {
        console.log('[PendingSwishWatcher] Order too old, clearing:', { orderId: pendingOrderId, ageMinutes });
        localStorage.removeItem('pending_swish_order_id');
        localStorage.removeItem('pending_swish_order_timestamp');
        return;
      }
    }

    // Prevent multiple simultaneous checks
    if (isChecking) return;

    // Verify order status before redirecting
    const checkOrderStatus = async () => {
      setIsChecking(true);
      try {
        let order: any = null;
        let error: any = null;

        // GUEST: Use Edge Function (bypasses RLS securely)
        // AUTHENTICATED: Use direct RLS query
        if (isGuest) {
          order = await fetchGuestOrderById(pendingOrderId);
        } else {
          const result = await supabase
            .from('trade_orders')
            .select('status, qr_key')
            .eq('id', pendingOrderId)
            .single();
          order = result.data;
          error = result.error;
        }

        if (error || !order) {
          console.log('[PendingSwishWatcher] Order not found, clearing localStorage:', pendingOrderId);
          localStorage.removeItem('pending_swish_order_id');
          localStorage.removeItem('pending_swish_order_timestamp');
          setIsChecking(false);
          return;
        }

        const status = order.status;
        console.log('[PendingSwishWatcher] Order status:', { orderId: pendingOrderId, status, hasQrKey: !!order.qr_key, isGuest });

        // Redirect to success page if payment is confirmed (with or without QR key).
        // If qr_key is missing but order is paid, the success page will show the retry button.
        if (order.qr_key || status === 'paid' || status === 'completed') {
          console.log('[PendingSwishWatcher] Payment confirmed, redirecting to success page', { hasQrKey: !!order.qr_key, status });
          localStorage.removeItem('pending_swish_order_id');
          localStorage.removeItem('pending_swish_order_timestamp');
          navigate(`/payment-success?order=${pendingOrderId}`, { replace: true });
        } else if (status === 'failed' || status === 'cancelled') {
          // Payment failed/cancelled - clear localStorage
          console.log('[PendingSwishWatcher] Payment failed/cancelled, clearing:', status);
          localStorage.removeItem('pending_swish_order_id');
          localStorage.removeItem('pending_swish_order_timestamp');
        }
        // For 'pending'/'authorized' status without QR - do nothing, keep waiting
        setIsChecking(false);
      } catch (err) {
        console.error('[PendingSwishWatcher] Error checking order:', err);
        setIsChecking(false);
      }
    };

    checkOrderStatus();
  }, [location.pathname, navigate, isChecking, isGuest]);

  return null;
}
